/**
 * PDP / quick-add: resolve latest open ddcs_term by rental_end_date, then matching
 * ddcs campus + term rows (getFilteredCampusTerm + metaobject fallbacks).
 *
 * Exposes cart line properties aligned with CMRP (product-facade getCmrpRentalCampusTermProperties):
 * _rental_refund_grace_period ← campus.rental_refund_grace_period
 * _rental_refund_date ← term.rental_refund_date
 * Callers append both keys even when values are empty strings (term/campus missing field).
 *
 * product-form.js and dialog.js await __pdpDdcsRentalReadyPromise before add-to-cart for rentals.
 */
(function () {
  'use strict';

  /** @type {(value?: unknown) => void} */
  let resolveDdcsRentalReady = () => {};
  window.__pdpDdcsRentalReadyPromise = new Promise((resolve) => {
    resolveDdcsRentalReady = resolve;
  });

  /**
   * @param {any} row
   * @param {string[]} keys
   * @returns {unknown}
   */
  function firstDefinedField(row, keys) {
    if (!row || typeof row !== 'object') return undefined;
    for (const k of keys) {
      if (k in row && row[k] != null && String(row[k]).trim() !== '') {
        return row[k];
      }
    }
    const f = row.fields;
    if (f && typeof f === 'object') {
      for (const k of keys) {
        if (k in f && f[k] != null && String(f[k]).trim() !== '') {
          return f[k];
        }
      }
    }
    return undefined;
  }

  /**
   * @param {any} campus
   * @returns {unknown}
   */
  function getCampusRentalRefundGracePeriod(campus) {
    return firstDefinedField(campus, [
      'rental_refund_grace_period',
      'rentalRefundGracePeriod',
      'rental_refund_grace',
    ]);
  }

  /**
   * @param {any} term
   * @returns {unknown}
   */
  function getTermRentalRefundDate(term) {
    return firstDefinedField(term, ['rental_refund_date', 'rentalRefundDate']);
  }

  /**
   * getFilteredCampusTerm often returns slim rows; the "latest term" from metaobjects is full.
   * When both refer to the same term_id, copy refund date from latest if the resolved row lacks it.
   * If no row matched the filter, fall back to latestTerm (still the correct open term for PDP).
   * @param {any | null} termRow
   * @param {any | null} latestTerm
   * @returns {any | null}
   */
  function resolveTermRowForRentalRefund(termRow, latestTerm) {
    if (!latestTerm) return termRow ?? null;
    const latestId = normId(latestTerm.term_id);
    if (!termRow) {
      return latestTerm;
    }
    const same =
      normId(termRow.term_id) === latestId ||
      normId(termRow.term) === latestId;
    if (!same) return termRow;
    const fromRow = getTermRentalRefundDate(termRow);
    if (fromRow != null && String(fromRow).trim() !== '') return termRow;
    const fromLatest = getTermRentalRefundDate(latestTerm);
    if (fromLatest == null || String(fromLatest).trim() === '') return termRow;
    return { ...termRow, rental_refund_date: fromLatest };
  }

  /**
   * Same property names as CMRP add-to-cart (product-facade.js).
   * @returns {{ _rental_refund_grace_period: string, _rental_refund_date: string }}
   */
  function getPdpRentalCampusTermLineProperties() {
    const campus = window.__pdpDdcsCampusForRental;
    const term = window.__pdpDdcsTermForRental;
    const grace = getCampusRentalRefundGracePeriod(campus);
    const refundDate = getTermRentalRefundDate(term);
    const out = {
      _rental_refund_grace_period: grace != null && String(grace) !== '' ? String(grace) : '',
      _rental_refund_date:
        refundDate != null && String(refundDate) !== '' ? String(refundDate) : '',
    };
    return out;
  }
  window.getPdpRentalCampusTermLineProperties = getPdpRentalCampusTermLineProperties;

  const metaobjectsUrl = '/apps/shopifyData/metaobjects';
  const filteredCampusTermUrl = '/apps/shopifyData/getFilteredCampusTerm';

  /**
   * @param {string} v
   * @returns {string}
   */
  function normId(v) {
    return String(v ?? '')
      .trim()
      .toLowerCase();
  }

  /**
   * Same response handling as page.courses-materials-results.liquid (fetchMetaobjectsData).
   * @returns {Promise<{ campuses: any[]; terms: any[] }>}
   */
  async function fetchFilteredCampusTermLists() {
    const response = await fetch(filteredCampusTermUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) {
      throw new Error(`getFilteredCampusTerm HTTP ${response.status}`);
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'getFilteredCampusTerm failed');
    }
    const raw = result.data ?? result;
    let campuses = raw.ddcs_campus;
    let terms = raw.ddcs_term;
    if ((!Array.isArray(campuses) || campuses.length === 0) && raw.data && typeof raw.data === 'object') {
      campuses = raw.data.ddcs_campus ?? campuses;
      terms = raw.data.ddcs_term ?? terms;
    }
    return {
      campuses: Array.isArray(campuses) ? campuses : [],
      terms: Array.isArray(terms) ? terms : [],
    };
  }

  /**
   * @param {string} campusEntityId
   * @param {string} termId
   * @param {any[]} campuses
   * @param {any[]} terms
   * @returns {{ campus: any | null; term: any | null }}
   */
  function pickFromFilteredLists(campusEntityId, termId, campuses, terms) {
    const ce = normId(campusEntityId);
    const tid = normId(termId);
    const campus = campuses.find((c) => normId(c.campus_entity_id) === ce) ?? null;
    const term =
      terms.find((t) => normId(t.term_id) === tid || normId(t.term) === tid) ?? null;
    return { campus, term };
  }

  /**
   * @param {string} metaobjectType
   * @param {string} query
   * @returns {Promise<any[]>}
   */
  async function fetchMetaobjects(metaobjectType, query) {
    const url = `${metaobjectsUrl}?type=${metaobjectType}&query=${encodeURIComponent(query)}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} (${metaobjectType})`);
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || `metaobjects failed (${metaobjectType})`);
    }
    const data = Array.isArray(result.data) ? result.data : [];
    const active = data.filter((item) => item.status === 'ACTIVE');
    if (active.length > 0) return active;
    /* Some payloads omit status or use non-ACTIVE; keep rows for debugging if search matched */
    return data;
  }

  /**
   * Product metafield ddcs.ddcs_campus_entity → definition handle is often ddcs_campus_entity, not ddcs_campus.
   * @param {string} campusEntityId
   * @returns {Promise<any | null>}
   */
  async function fetchDdcsCampusByEntityIdViaMetaobjects(campusEntityId) {
    const q = `fields.campus_entity_id:'${campusEntityId}' fields.disabled_flag:false`;
    for (const type of ['ddcs_campus_entity', 'ddcs_campus']) {
      try {
        const rows = await fetchMetaobjects(type, q);
        if (rows.length > 0) return rows[0];
      } catch {
        /* type may not exist on this shop */
      }
    }
    return null;
  }

  /**
   * url-state-manager findMatchingEntities + variant-textbook-picker style.
   * @param {string} termId
   * @returns {Promise<any | null>}
   */
  async function fetchDdcsTermByTermIdViaMetaobjects(termId) {
    const qOr = `(fields.term:'${termId}' OR fields.term_id:'${termId}') fields.disabled_flag:false`;
    const rows = await fetchMetaobjects('ddcs_term', qOr);
    if (rows.length > 0) return rows[0];
    const qIdOnly = `fields.term_id:'${termId}' fields.disabled_flag:false`;
    const rows2 = await fetchMetaobjects('ddcs_term', qIdOnly);
    return rows2[0] ?? null;
  }

  /**
   * @returns {Promise<any | null>}
   */
  async function getLatestTermByRentalEndDate() {
    const termQuery = 'fields.disabled_flag:false fields.is_term_open_flag:true';
    const termUrl = `${metaobjectsUrl}?type=ddcs_term&query=${encodeURIComponent(termQuery)}`;

    const termResponse = await fetch(termUrl, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    });

    if (!termResponse.ok) {
      console.warn('[pdp-latest-term] ddcs_term request failed', termResponse.status);
      return null;
    }

    const termResult = await termResponse.json();
    if (!termResult.success || !Array.isArray(termResult.data) || termResult.data.length === 0) {
      console.warn('[pdp-latest-term] No ddcs_term rows returned');
      return null;
    }

    /** @type {any} */
    let latestTerm = null;
    let latestTs = -Infinity;

    for (const term of termResult.data) {
      const end = term && term.rental_end_date;
      if (end == null || String(end).trim() === '') continue;
      const ts = new Date(end).getTime();
      if (Number.isNaN(ts)) continue;
      if (ts >= latestTs) {
        latestTs = ts;
        latestTerm = term;
      }
    }

    return latestTerm;
  }

  /**
   * @param {any} latestTerm
   */
  async function logResolvedDdcsMetaobjects(latestTerm) {
    const campusEntityId = latestTerm && latestTerm.campus_entity_id;
    const termId = latestTerm && latestTerm.term_id;
    if (!campusEntityId || !termId) {
      console.warn('[pdp-latest-term] Latest term missing campus_entity_id or term_id; skip resolution');
      window.__pdpDdcsCampusForRental = null;
      window.__pdpDdcsTermForRental = null;
      return;
    }

    const cStr = String(campusEntityId);
    const tStr = String(termId);

    try {
      let campusRow = null;
      let termRow = null;
      /** @type {string} */
      let campusFrom = 'none';
      /** @type {string} */
      let termFrom = 'none';

      try {
        const { campuses, terms } = await fetchFilteredCampusTermLists();
        const picked = pickFromFilteredLists(cStr, tStr, campuses, terms);
        if (picked.campus) {
          campusRow = picked.campus;
          campusFrom = 'getFilteredCampusTerm';
        }
        if (picked.term) {
          termRow = picked.term;
          termFrom = 'getFilteredCampusTerm';
        }
      } catch (e) {
        console.warn('[pdp-latest-term] getFilteredCampusTerm failed; will try metaobjects only', e);
      }

      if (!campusRow) {
        campusRow = await fetchDdcsCampusByEntityIdViaMetaobjects(cStr);
        if (campusRow) campusFrom = 'metaobjects';
      }
      if (!termRow) {
        termRow = await fetchDdcsTermByTermIdViaMetaobjects(tStr);
        if (termRow) termFrom = 'metaobjects';
      }

      window.__pdpDdcsCampusForRental = campusRow;
      window.__pdpDdcsTermForRental = resolveTermRowForRentalRefund(termRow, latestTerm);
  
    } catch (err) {
      console.warn('[pdp-latest-term] Failed to resolve campus / term rows', err);
      window.__pdpDdcsCampusForRental = null;
      window.__pdpDdcsTermForRental = null;
    }
  }

  async function logLatestTermOnPdp() {
    try {
      const term = await getLatestTermByRentalEndDate();
      if (term) {
        await logResolvedDdcsMetaobjects(term);
      } else {
        window.__pdpDdcsCampusForRental = null;
        window.__pdpDdcsTermForRental = null;
      }
    } catch (err) {
      console.error('[pdp-latest-term]', err);
      window.__pdpDdcsCampusForRental = null;
      window.__pdpDdcsTermForRental = null;
    }
  }

  async function bootstrapPdpDdcsRentalContext() {
    try {
      await logLatestTermOnPdp();
    } finally {
      resolveDdcsRentalReady();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      void bootstrapPdpDdcsRentalContext();
    });
  } else {
    void bootstrapPdpDdcsRentalContext();
  }

  window.getLatestTermByRentalEndDate = getLatestTermByRentalEndDate;
  window.fetchFilteredCampusTermLists = fetchFilteredCampusTermLists;
  window.fetchDdcsCampusByEntityIdViaMetaobjects = fetchDdcsCampusByEntityIdViaMetaobjects;
  window.fetchDdcsTermByTermIdViaMetaobjects = fetchDdcsTermByTermIdViaMetaobjects;
})();
